/**
 * Multi-select customer filter for the Agreements tab.
 *
 * Asked for by Stacie (2026-08-09): the tab lists every customer's agreements and
 * there was no way to narrow to the accounts you are actually working. A single-
 * select would have been less code, but multi-select with one row ticked IS
 * single-select, so this shape cannot be the wrong one.
 *
 * Search is SERVER-side (`/api/customers?q=`). The customer base is far larger
 * than one page, so filtering a fetched page in the browser would silently hide
 * matches — you would type a customer's name and be told there is no such
 * customer. The cap is surfaced in the panel rather than left implicit.
 *
 * Capability note: `/api/customers` is gated on `view:customers` and this panel
 * lives behind `view:supplier_tab`. Every role that holds the latter (admin,
 * manager, csm, btp_only, viewer) also holds the former — checked against
 * ROLE_CAPS in customer-management-api/src/auth/roles.ts rather than assumed, so
 * the picker cannot 403 for someone who can reach the page it sits on.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { apiFetch, ApiError } from '@/features/crm/lib/api-fetch';

interface PickerCustomer {
  customer_id: string;
  name: string;
  account_number: string | null;
  city: string | null;
  state: string | null;
}

/**
 * `/api/customers`' response.
 *
 * `truncated` is the server's OWN answer to "is this a page or the whole match
 * set", and it is what this component uses. It arrived on staging 2026-08-10 in
 * the same change that gave `total` honest semantics: `total` used to be
 * `customers.length`, so any client comparing the two got `n > n` — always false.
 * This panel's "showing N of M" note was written against that and could therefore
 * never fire. Reading `truncated` instead of re-deriving it means there is one
 * definition of truncation rather than two that can disagree.
 */
interface CustomerSearchResult {
  customers: PickerCustomer[];
  /** How many customers MATCH — not how many came back. */
  total: number;
  returned: number;
  truncated: boolean;
  limit: number;
}

/**
 * How many customers the panel lists. Matches `/api/customers`' own default so
 * the "first N" note below states the real number rather than a guess.
 */
const PAGE = 50;

export interface CustomerPickerProps {
  /** Currently-filtered customer ids, in the order they appear in the URL. */
  selected: readonly string[];
  /** Called with the full next selection — never a delta. */
  onChange: (ids: string[]) => void;
  /**
   * Names already known from elsewhere, keyed by id. The agreement rows carry
   * `customer.name`, which is what lets a chip for a `?customer_id=` that arrived
   * by link render a readable name instead of a bare uuid.
   */
  nameHints?: Readonly<Record<string, string>>;
  /** Upper bound the API enforces; selecting past it is blocked here too. */
  max: number;
}

export function CustomerPicker({
  selected,
  onChange,
  nameHints,
  max,
}: CustomerPickerProps) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [results, setResults] = useState<PickerCustomer[]>([]);
  const [total, setTotal] = useState(0);
  /** The server's own truncation verdict — see CustomerSearchResult. */
  const [capped, setCapped] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * id → name for everything we have ever seen, so a chip keeps its label after
   * the search that produced it is replaced. Without this, ticking a customer and
   * then typing a new search turns that customer's chip back into a raw id.
   */
  const [knownNames, setKnownNames] = useState<Record<string, string>>({});

  const containerRef = useRef<HTMLDivElement>(null);

  // Debounced server-side search. Only runs while the panel is open — a closed
  // panel firing customer searches on every keystroke of the page's other inputs
  // would be pure waste.
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const handle = setTimeout(() => {
      setLoading(true);
      setError(null);
      const term = q.trim();
      const url = term
        ? `/api/customers?limit=${PAGE}&q=${encodeURIComponent(term)}`
        : `/api/customers?limit=${PAGE}`;
      apiFetch<CustomerSearchResult>(url)
        .then((d) => {
          if (cancelled) return;
          const rows = d.customers ?? [];
          setResults(rows);
          setTotal(d.total ?? rows.length);
          // `?? d.total > rows.length` only as a fallback for an older API that
          // has not shipped `truncated` yet. Never `?? false`: defaulting to "not
          // truncated" would present a page as the whole customer base, which is
          // the failure this note exists to prevent.
          setCapped(d.truncated ?? (d.total ?? 0) > rows.length);
          setKnownNames((prev) => {
            const next = { ...prev };
            for (const c of rows) if (c.name) next[c.customer_id] = c.name;
            return next;
          });
        })
        .catch((e) => {
          if (!cancelled) {
            // Surfaced in the panel, not as a page-level banner: the agreements
            // table below is still correct and usable, and replacing it with a red
            // banner because a filter's lookup failed would be the worse outcome.
            setError(e instanceof ApiError ? e.message : String(e));
            setResults([]);
          }
        })
        .finally(() => {
          if (!cancelled) setLoading(false);
        });
    }, 250);
    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [q, open]);

  // Close on outside click and on Escape. Both, because either alone leaves a way
  // to get stuck with the panel covering the table.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const selectedSet = useMemo(() => new Set(selected), [selected]);

  const labelFor = (id: string) => knownNames[id] ?? nameHints?.[id] ?? id;

  const toggle = (id: string) => {
    if (selectedSet.has(id)) {
      onChange(selected.filter((s) => s !== id));
      return;
    }
    // Refuse rather than truncate. Silently dropping the 51st pick would show a
    // filtered list that does not match the chips beside it.
    if (selected.length >= max) return;
    onChange([...selected, id]);
  };

  const atCap = selected.length >= max;

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <button
        aria-expanded={open}
        aria-haspopup="true"
        className={'pill' + (selected.length > 0 ? ' active' : '')}
        data-active={selected.length > 0 ? 'true' : 'false'}
        data-testid="supplier-agreements-customer-picker-toggle"
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        {selected.length === 0
          ? 'Pick customers'
          : `${selected.length} customer${selected.length === 1 ? '' : 's'}`}
        <span aria-hidden="true" style={{ marginLeft: 6 }}>
          ▾
        </span>
      </button>

      {open && (
        <div
          data-testid="supplier-agreements-customer-picker-panel"
          style={{
            position: 'absolute',
            zIndex: 20,
            top: 'calc(100% + 6px)',
            left: 0,
            width: 340,
            maxHeight: 380,
            display: 'flex',
            flexDirection: 'column',
            // --bg-card, a real derived token. NOT a raw --surface-* one: those
            // are bare HSL triplets, so `background: var(--surface-midground)`
            // computes to TRANSPARENT — a floating panel you can read the table
            // through, which no test in jsdom can see.
            background: 'var(--bg-card)',
            // --text-muted, not a --border* token: every border token in this
            // stylesheet resolves to neutral-200, which measures ~1.2:1 against
            // the table behind it and so fails WCAG 1.4.11's 3.0 for the boundary
            // that identifies a control. See crm/disclosure-affordance.spec.ts.
            border: '1px solid var(--text-muted)',
            borderRadius: 6,
            boxShadow: '0 8px 24px rgba(0,0,0,0.18)',
          }}
        >
          <div
            style={{ padding: 10, borderBottom: '1px solid var(--hairline)' }}
          >
            <input
              aria-label="Search customers"
              autoFocus
              className="input"
              data-testid="supplier-agreements-customer-picker-search"
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name, account or domain…"
              style={{ width: '100%' }}
              type="search"
              value={q}
            />
          </div>

          <div style={{ overflowY: 'auto', flex: 1, padding: '4px 0' }}>
            {error && (
              <div
                data-testid="supplier-agreements-customer-picker-error"
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}
              >
                Customer search is unavailable right now — {error}
              </div>
            )}

            {!error && loading && results.length === 0 && (
              <div
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: 'var(--text-secondary)',
                }}
              >
                Searching…
              </div>
            )}

            {!error && !loading && results.length === 0 && (
              <div
                data-testid="supplier-agreements-customer-picker-empty"
                style={{
                  padding: '10px 12px',
                  fontSize: 12,
                  color: 'var(--text-primary)',
                }}
              >
                No customers match &ldquo;{q.trim()}&rdquo;.
              </div>
            )}

            {results.map((c) => {
              const checked = selectedSet.has(c.customer_id);
              // A row that cannot be ticked because the cap is reached is
              // disabled and SAYS why, rather than being a control that silently
              // does nothing when clicked.
              const blocked = !checked && atCap;
              return (
                <label
                  key={c.customer_id}
                  style={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 8,
                    // 24px minimum interactive height — WCAG 2.5.8 Target Size.
                    minHeight: 24,
                    padding: '6px 12px',
                    cursor: blocked ? 'not-allowed' : 'pointer',
                    opacity: blocked ? 0.55 : 1,
                  }}
                  title={blocked ? `At most ${max} customers` : c.customer_id}
                >
                  <input
                    checked={checked}
                    data-testid={`supplier-agreements-customer-option-${c.customer_id}`}
                    disabled={blocked}
                    onChange={() => toggle(c.customer_id)}
                    style={{ marginTop: 3 }}
                    type="checkbox"
                  />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 13,
                        color: 'var(--text-primary)',
                      }}
                    >
                      {c.name}
                    </span>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 11,
                        color: 'var(--text-secondary)',
                      }}
                    >
                      {[
                        c.account_number,
                        [c.city, c.state].filter(Boolean).join(', '),
                      ]
                        .filter(Boolean)
                        .join(' · ') || c.customer_id}
                    </span>
                  </span>
                </label>
              );
            })}
          </div>

          {/* Says when the list is a page rather than the whole match set. Without
              this the panel looks like an exhaustive list of who exists, and a
              customer absent from it reads as a customer absent from the system. */}
          {capped && !error && (
            <div
              data-testid="supplier-agreements-customer-picker-capped"
              style={{
                padding: '8px 12px',
                borderTop: '1px solid var(--hairline)',
                fontSize: 11,
                color: 'var(--text-primary)',
              }}
            >
              Showing {results.length.toLocaleString()} of{' '}
              {total.toLocaleString()} matches — keep typing to narrow it.
            </div>
          )}

          {atCap && (
            <div
              data-testid="supplier-agreements-customer-picker-at-cap"
              style={{
                padding: '8px 12px',
                borderTop: '1px solid var(--hairline)',
                fontSize: 11,
                color: 'var(--text-primary)',
              }}
            >
              At the {max}-customer limit. Clear one to pick another.
            </div>
          )}
        </div>
      )}

      {/* Chips live OUTSIDE the panel so the active filter stays visible once it
          closes. A filter you cannot see is the "why is this queue so short?"
          support question. */}
      {selected.length > 0 && (
        <div
          data-testid="supplier-agreements-customer-chips"
          style={{
            display: 'flex',
            gap: 6,
            flexWrap: 'wrap',
            alignItems: 'center',
            marginTop: 8,
          }}
        >
          {selected.map((id) => (
            <span className="badge badge-blue" key={id} title={id}>
              {labelFor(id)}{' '}
              <button
                aria-label={`Remove ${labelFor(id)} from the customer filter`}
                className="row-link"
                data-testid={`supplier-agreements-customer-chip-remove-${id}`}
                onClick={() => onChange(selected.filter((s) => s !== id))}
                type="button"
              >
                ✕
              </button>
            </span>
          ))}
          <button
            className="row-link"
            data-testid="supplier-agreements-customer-clear-all"
            onClick={() => onChange([])}
            style={{ fontSize: 12 }}
            type="button"
          >
            Clear all
          </button>
        </div>
      )}
    </div>
  );
}
